import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import CableInstanceEditModal from '../modals/CableInstanceEditModal'
import CableSpecEditModal from '../modals/CableSpecEditModal'
import DeviceInstanceEditModal from '../modals/DeviceInstanceEditModal'
import DeviceSpecEditModal from '../modals/DeviceSpecEditModal'
import { getDeviceInstanceById } from '../../api/deviceInstances'
import { getSpecificationItem, type Specification } from '../../api/specification'

export type SchemaElementInfoEntityType = 'device' | 'cable'

export interface SchemaElementInfoAsideProps {
  projectId: number
  deviceInstanceId: number
  deviceName: string
  specificationId?: number
  specName?: string
  entityType?: SchemaElementInfoEntityType
  description?: string
  knxAddress?: string
  isReserve?: boolean
  deviceTypeId?: number
  onClose: () => void
  onRemove?: () => void
  onDataChanged?: () => void
  isReadOnly?: boolean
  children?: ReactNode
  className?: string
}

async function copyTextToClipboard(textToCopy: string, label: string) {
  try {
    await navigator.clipboard.writeText(textToCopy)
  } catch (err) {
    console.error(`Failed to copy ${label}:`, err)
    const textArea = document.createElement('textarea')
    textArea.value = textToCopy
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
  }
}

export default function SchemaElementInfoAside({
  projectId,
  deviceInstanceId,
  deviceName,
  specificationId,
  specName,
  entityType = 'device',
  description,
  knxAddress,
  isReserve,
  deviceTypeId,
  onClose,
  onRemove,
  onDataChanged,
  isReadOnly = false,
  children,
  className = 'w-80 border-r border-gray-200 bg-white flex flex-col shadow-lg h-full max-h-screen',
}: SchemaElementInfoAsideProps) {
  const queryClient = useQueryClient()
  const [showInstanceEditModal, setShowInstanceEditModal] = useState(false)
  const [showSpecEditModal, setShowSpecEditModal] = useState(false)
  const [selectedSpecification, setSelectedSpecification] = useState<Specification | null>(null)
  const [selectedElementSpecification, setSelectedElementSpecification] = useState<Specification | null>(null)

  const resolvedSpecId = specificationId && specificationId > 0 ? specificationId : undefined

  useEffect(() => {
    if (!resolvedSpecId) {
      setSelectedElementSpecification(null)
      return
    }
    getSpecificationItem(resolvedSpecId)
      .then((spec) => setSelectedElementSpecification(spec))
      .catch((error) => {
        console.error('Error loading specification for element info:', error)
        setSelectedElementSpecification(null)
      })
  }, [resolvedSpecId])

  const { data: cableInstanceData } = useQuery({
    queryKey: ['cableInstance', deviceInstanceId],
    queryFn: () => getDeviceInstanceById(deviceInstanceId),
    enabled: showInstanceEditModal && entityType === 'cable' && !!deviceInstanceId,
  })

  const cableInstanceForModal = useMemo(() => {
    if (cableInstanceData) return cableInstanceData
    if (entityType !== 'cable') return null
    return {
      id: deviceInstanceId,
      position_label: deviceName,
      specification_id: resolvedSpecId,
      spec_name: specName,
      description: description || '',
      is_reserve: isReserve ? 1 : 0,
      project_id: projectId,
    } as any
  }, [cableInstanceData, entityType, deviceInstanceId, deviceName, resolvedSpecId, specName, description, isReserve, projectId])

  const invalidateAfterEdit = useCallback(() => {
    onDataChanged?.()
    queryClient.invalidateQueries({ queryKey: ['deviceInstances', projectId] })
    queryClient.invalidateQueries({ queryKey: ['availableDevices', projectId] })
    queryClient.invalidateQueries({ queryKey: ['availableCables', projectId] })
    if (resolvedSpecId) {
      getSpecificationItem(resolvedSpecId)
        .then((spec) => setSelectedElementSpecification(spec))
        .catch(() => setSelectedElementSpecification(null))
    }
  }, [onDataChanged, projectId, queryClient, resolvedSpecId])

  const manufacturer = selectedElementSpecification?.manufacturer
  const model = selectedElementSpecification?.model

  return (
    <>
      <aside className={`element-info-panel flex flex-col z-10 relative ${className}`}>
        <div className="panel-header bg-gray-50 px-4 py-3 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Информация об элементе</h3>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
              ✕
            </button>
          </div>
        </div>

        <div className="panel-content flex-1 flex flex-col p-4 overflow-y-auto">
          <div className="space-y-3">
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => window.open(`/projects/${projectId}/device-instances/${deviceInstanceId}`, '_blank')}
                className="flex-shrink-0 p-1 text-gray-600 hover:bg-gray-50 rounded transition-colors"
                title="Просмотр экземпляра"
              >
                👁️
              </button>
              {!isReadOnly && (
                <button
                  type="button"
                  onClick={() => setShowInstanceEditModal(true)}
                  className="flex-shrink-0 p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  title="Редактировать экземпляр"
                >
                  ✏️
                </button>
              )}
              <div className="flex-1 flex items-center min-w-0">
                <span
                  onClick={() => copyTextToClipboard(String(deviceInstanceId), 'ID')}
                  className="text-blue-600 font-mono font-medium hover:bg-blue-50 px-1 py-0.5 rounded cursor-pointer"
                  title="Кликните для копирования ID"
                >
                  {deviceInstanceId}:
                </span>
                <span
                  onClick={() => copyTextToClipboard(deviceName, 'name')}
                  className="ml-2 text-gray-900 font-medium hover:bg-gray-50 px-1 py-0.5 rounded cursor-pointer truncate"
                  title="Кликните для копирования названия"
                >
                  {deviceName || '—'}
                  {description ? (
                    <span className="font-normal text-gray-600"> ({description})</span>
                  ) : null}
                </span>
              </div>
            </div>

            {resolvedSpecId ? (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => window.open(`/projects/${projectId}/devices/${resolvedSpecId}`, '_blank')}
                  className="flex-shrink-0 p-1 text-gray-600 hover:bg-gray-50 rounded transition-colors"
                  title="Просмотр спецификации"
                >
                  👁️
                </button>
                {!isReadOnly && (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const specData = await getSpecificationItem(resolvedSpecId)
                        setSelectedSpecification(specData)
                        setShowSpecEditModal(true)
                      } catch (error) {
                        console.error('Error loading specification:', error)
                        alert(
                          `Ошибка: Спецификация с ID ${resolvedSpecId} не найдена.\n\nЭлемент: ${deviceName}\nТип: ${specName || '—'}`,
                        )
                      }
                    }}
                    className="flex-shrink-0 p-1 text-green-600 hover:bg-green-50 rounded transition-colors"
                    title="Редактировать спецификацию"
                  >
                    ✏️
                  </button>
                )}
                <div className="flex-1 flex items-center min-w-0">
                  <span
                    onClick={() => copyTextToClipboard(String(resolvedSpecId), 'spec ID')}
                    className="text-green-600 font-mono font-medium hover:bg-green-50 px-1 py-0.5 rounded cursor-pointer"
                    title="Кликните для копирования ID спецификации"
                  >
                    {resolvedSpecId}:
                  </span>
                  <span
                    onClick={() => copyTextToClipboard(specName || '', 'spec name')}
                    className="ml-2 text-gray-900 hover:bg-gray-50 px-1 py-0.5 rounded cursor-pointer truncate"
                    title="Кликните для копирования названия спецификации"
                  >
                    {specName || '—'}
                  </span>
                </div>
              </div>
            ) : null}

            {(manufacturer || model) && (
              <div className="flex items-center gap-1">
                <div className="flex-1 flex flex-wrap items-center text-sm text-gray-600 gap-x-2">
                  {manufacturer ? (
                    <span>
                      <span className="font-medium">Производитель:</span>{' '}
                      <span
                        onClick={() => copyTextToClipboard(manufacturer, 'manufacturer')}
                        className="hover:bg-gray-50 px-1 py-0.5 rounded cursor-pointer"
                        title="Кликните для копирования производителя"
                      >
                        {manufacturer}
                      </span>
                    </span>
                  ) : null}
                  {model ? (
                    <span>
                      <span className="font-medium">Модель:</span>{' '}
                      <span
                        onClick={() => copyTextToClipboard(model, 'model')}
                        className="hover:bg-gray-50 px-1 py-0.5 rounded cursor-pointer"
                        title="Кликните для копирования модели"
                      >
                        {model}
                      </span>
                    </span>
                  ) : null}
                </div>
              </div>
            )}

            {selectedElementSpecification?.documentation_url ? (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => window.open(selectedElementSpecification.documentation_url, '_blank')}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-md transition-colors text-sm font-medium"
                  title="Открыть документацию"
                >
                  📖 Документация
                </button>
              </div>
            ) : null}

            {children}
          </div>

          {onRemove && !isReadOnly ? (
            <div className="mt-6 pt-4 border-t flex-shrink-0">
              <button
                type="button"
                onClick={onRemove}
                className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
              >
                🗑️ Убрать с холста
              </button>
            </div>
          ) : null}
        </div>
      </aside>

      {entityType === 'cable' ? (
        <CableInstanceEditModal
          isOpen={showInstanceEditModal}
          onClose={() => setShowInstanceEditModal(false)}
          selectedInstance={cableInstanceData || cableInstanceForModal}
          projectId={projectId}
          onSuccess={() => {
            setShowInstanceEditModal(false)
            invalidateAfterEdit()
          }}
        />
      ) : (
        <DeviceInstanceEditModal
          isOpen={showInstanceEditModal}
          onClose={() => setShowInstanceEditModal(false)}
          selectedInstance={{
            id: deviceInstanceId,
            position_label: deviceName,
            specification_id: resolvedSpecId,
            device_type: 'device',
            device_type_id: deviceTypeId,
            spec_name: specName,
            manufacturer,
            model,
            knx_address: knxAddress,
            description: description || '',
            is_reserve: isReserve ? 1 : 0,
            project_id: projectId,
            created_at: new Date().toISOString(),
          } as any}
          projectId={projectId}
          onSuccess={() => {
            setShowInstanceEditModal(false)
            invalidateAfterEdit()
          }}
        />
      )}

      {selectedSpecification?.device_type === 'cable' ? (
        <CableSpecEditModal
          isOpen={showSpecEditModal}
          onClose={() => {
            setShowSpecEditModal(false)
            setSelectedSpecification(null)
          }}
          selectedCable={selectedSpecification}
          projectId={projectId}
          onSuccess={() => {
            setShowSpecEditModal(false)
            setSelectedSpecification(null)
            invalidateAfterEdit()
          }}
        />
      ) : (
        <DeviceSpecEditModal
          isOpen={showSpecEditModal}
          onClose={() => {
            setShowSpecEditModal(false)
            setSelectedSpecification(null)
          }}
          selectedDevice={selectedSpecification}
          projectId={projectId}
          onSuccess={() => {
            setShowSpecEditModal(false)
            setSelectedSpecification(null)
            invalidateAfterEdit()
          }}
        />
      )}
    </>
  )
}
